import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, FileText, CheckCircle2, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { useToast } from '../../src/contexts/ToastContext';

interface BoletoModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    boleto?: any | null;
}

const BoletoModal: React.FC<BoletoModalProps> = ({ isOpen, onClose, onSave, boleto }) => {
    const { addToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1: Cliente, 2: Resumo, 3: Detalhes, 4: Anexos

    // Form Data
    const [formData, setFormData] = useState({
        cliente_nome: '',
        cpf_cnpj: '',
        endereco: '',
        cidade_cep: '',
        unidade_consumidora: '',
        leitura_atual: '',
        leitura_anterior: '',
        bandeira: '',
        numero_cobranca: '',
        mes_referencia: '',

        // Resumo
        valor_total: '',
        valor_tarifa_distribuidora: '',
        data_emissao: new Date().toISOString().split('T')[0],
        data_vencimento: '',
        tem_desconto_quitacao: false,

        // Descontos
        desc_quitacao_data_pgt_1: '', desc_quitacao_desconto_percent_1: '', desc_quitacao_desconto_valor_1: '', desc_quitacao_a_pagar_1: '',
        desc_quitacao_data_pgt_2: '', desc_quitacao_desconto_percent_2: '', desc_quitacao_desconto_valor_2: '', desc_quitacao_a_pagar_2: '',
        desc_quitacao_data_pgt_3: '', desc_quitacao_desconto_percent_3: '', desc_quitacao_desconto_valor_3: '', desc_quitacao_a_pagar_3: '',

        // Detalhes da Cobrança
        detalhes_injecao_scee_qtd: '', detalhes_injecao_scee_tarifa: '', detalhes_injecao_scee_valor: '',
        detalhes_adc_bandeira_qtd: '', detalhes_adc_bandeira_tarifa: '', detalhes_adc_bandeira_valor: '',
        detalhes_imposto_nao_comp_valor: '',
        detalhes_energia_comp_nao_isenta_valor: '',
        detalhes_energia_comp_gd2_qtd: '', detalhes_energia_comp_gd2_valor: '',
        detalhes_outros_1_valor: '',
        detalhes_outros_2_valor: '',

        // Detalhes Adicionais
        consumo_mes: '',
        energia_compensada: '',
        valor_sem_desconto: '',
        valor_com_desconto: '',
        economia: '',

        // Arquivos
        codigo_barras: '',
        codigo_barras_anexo: '',
        qr_code_pix_anexo: ''
    });

    const [files, setFiles] = useState<{
        codigo_barras: File | null;
        qr_code: File | null;
    }>({
        codigo_barras: null,
        qr_code: null
    });

    React.useEffect(() => {
        if (boleto) {
            setFormData({
                cliente_nome: boleto.cliente_nome || boleto.fatura?.cliente_nome || '',
                cpf_cnpj: boleto.cpf_cnpj || '',
                endereco: boleto.endereco || '',
                cidade_cep: boleto.cidade_cep || '',
                unidade_consumidora: boleto.unidade_consumidora || '',
                leitura_atual: boleto.leitura_atual || '',
                leitura_anterior: boleto.leitura_anterior || '',
                bandeira: boleto.bandeira || '',
                numero_cobranca: boleto.numero_cobranca || '',
                mes_referencia: boleto.mes_referencia || '',

                valor_total: boleto.valor_total || '',
                valor_tarifa_distribuidora: boleto.valor_tarifa_distribuidora || '',
                data_emissao: boleto.data_emissao || new Date().toISOString().split('T')[0],
                data_vencimento: boleto.data_vencimento || '',
                tem_desconto_quitacao: boleto.tem_desconto_quitacao || false,

                desc_quitacao_data_pgt_1: boleto.desc_quitacao_data_pgt_1 || '',
                desc_quitacao_desconto_percent_1: boleto.desc_quitacao_desconto_percent_1 || '',
                desc_quitacao_desconto_valor_1: boleto.desc_quitacao_desconto_valor_1 || '',
                desc_quitacao_a_pagar_1: boleto.desc_quitacao_a_pagar_1 || '',

                desc_quitacao_data_pgt_2: boleto.desc_quitacao_data_pgt_2 || '',
                desc_quitacao_desconto_percent_2: boleto.desc_quitacao_desconto_percent_2 || '',
                desc_quitacao_desconto_valor_2: boleto.desc_quitacao_desconto_valor_2 || '',
                desc_quitacao_a_pagar_2: boleto.desc_quitacao_a_pagar_2 || '',

                desc_quitacao_data_pgt_3: boleto.desc_quitacao_data_pgt_3 || '',
                desc_quitacao_desconto_percent_3: boleto.desc_quitacao_desconto_percent_3 || '',
                desc_quitacao_desconto_valor_3: boleto.desc_quitacao_desconto_valor_3 || '',
                desc_quitacao_a_pagar_3: boleto.desc_quitacao_a_pagar_3 || '',

                detalhes_injecao_scee_qtd: boleto.detalhes_injecao_scee_qtd || '',
                detalhes_injecao_scee_tarifa: boleto.detalhes_injecao_scee_tarifa || '',
                detalhes_injecao_scee_valor: boleto.detalhes_injecao_scee_valor || '',
                detalhes_adc_bandeira_qtd: boleto.detalhes_adc_bandeira_qtd || '',
                detalhes_adc_bandeira_tarifa: boleto.detalhes_adc_bandeira_tarifa || '',
                detalhes_adc_bandeira_valor: boleto.detalhes_adc_bandeira_valor || '',
                detalhes_imposto_nao_comp_valor: boleto.detalhes_imposto_nao_comp_valor || '',
                detalhes_energia_comp_nao_isenta_valor: boleto.detalhes_energia_comp_nao_isenta_valor || '',
                detalhes_energia_comp_gd2_qtd: boleto.detalhes_energia_comp_gd2_qtd || '',
                detalhes_energia_comp_gd2_valor: boleto.detalhes_energia_comp_gd2_valor || '',
                detalhes_outros_1_valor: boleto.detalhes_outros_1_valor || '',
                detalhes_outros_2_valor: boleto.detalhes_outros_2_valor || '',

                consumo_mes: boleto.consumo_mes || '',
                energia_compensada: boleto.energia_compensada || '',
                valor_sem_desconto: boleto.valor_sem_desconto || '',
                valor_com_desconto: boleto.valor_com_desconto || '',
                economia: boleto.economia || '',
                codigo_barras: boleto.codigo_barras || '',
                codigo_barras_anexo: boleto.codigo_barras_anexo || '',
                qr_code_pix_anexo: boleto.qr_code_pix_anexo || ''
            });
        }
    }, [boleto]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'codigo_barras' | 'qr_code') => {
        if (e.target.files && e.target.files[0]) {
            setFiles(prev => ({
                ...prev,
                [type]: e.target.files![0]
            }));
        }
    };

    const uploadFile = async (file: File, path: string) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `${path}/${fileName}`;

        const { error: uploadError, data } = await supabase.storage
            .from('faturas') // Using 'faturas' bucket for now
            .upload(filePath, file);

        if (uploadError) throw uploadError;
        return data.path;
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            let codigo_barras_url = '';
            let qr_code_url = '';

            // Upload files
            if (files.codigo_barras) {
                codigo_barras_url = await uploadFile(files.codigo_barras, 'boletos/barras');
            }
            if (files.qr_code) {
                qr_code_url = await uploadFile(files.qr_code, 'boletos/qrcode');
            }

            // Sanitize data: Convert empty strings to null for nullable fields
            const sanitizedData = Object.entries(formData).reduce((acc, [key, value]) => {
                // Keep boolean values as is
                if (typeof value === 'boolean') {
                    acc[key] = value;
                } else {
                    // Convert empty strings to null, otherwise keep value
                    acc[key] = value === '' ? null : value;
                }
                return acc;
            }, {} as any);

            // Insert or Update into Database
            if (boleto?.id) {
                const { error } = await supabase
                    .from('boletos_vigor')
                    .update({
                        ...sanitizedData,
                        ...(codigo_barras_url && { codigo_barras_anexo: codigo_barras_url }),
                        ...(qr_code_url && { qr_code_pix_anexo: qr_code_url }),
                    })
                    .eq('id', boleto.id);

                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('boletos_vigor')
                    .insert([{
                        ...sanitizedData,
                        codigo_barras_anexo: codigo_barras_url || null,
                        qr_code_pix_anexo: qr_code_url || null,
                        status: 'gerado', // Default status
                        created_at: new Date().toISOString()
                    }]);

                if (error) throw error;
            }

            // Log Action
            const { data: { user } } = await supabase.auth.getUser();
            if (boleto?.id) {
                await logAction(user?.id, 'UPDATE_BOLETO', {
                    boleto_id: boleto.id,
                    cliente: formData.cliente_nome
                });
                logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'boletos', acao: 'atualizar_boleto', origem: 'acao' });
            } else {
                await logAction(user?.id, 'CREATE_BOLETO', {
                    cliente: formData.cliente_nome
                });
                logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'boletos', acao: 'criar_boleto', origem: 'acao' });
            }

            onSave();
            onClose();
        } catch (error: any) {
            console.error('Error saving boleto:', error);
            addToast(`Erro ao salvar boleto: ${error.message || 'Verifique os dados.'}`, 'error');
        } finally {
            setLoading(false);
        }
    };



    return createPortal(
        <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4 overflow-y-auto ${!isOpen ? 'hidden' : ''}`}>
            <div className="bg-white dark:bg-gray-800/95 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto flex flex-col transition-colors duration-200 border dark:border-gray-700">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-100 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800/80 backdrop-blur-md z-10 transition-colors duration-200">
                    <div>
                        <h2 className="text-xl font-bold font-poppins text-gray-800 dark:text-white">{boleto ? 'Editar Boleto' : 'Novo Boleto Vigor'}</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{boleto ? 'Atualize as informações do boleto' : 'Preencha os dados do novo boleto'}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                        <X size={20} className="text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-8">

                    {/* Section 1: Dados do Cliente */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">Dados do Cliente</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Nome do Cliente</label>
                                <input type="text" name="cliente_nome" value={formData.cliente_nome} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">CPF/CNPJ</label>
                                <input type="text" name="cpf_cnpj" value={formData.cpf_cnpj} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Unidade Consumidora (UC)</label>
                                <input type="number" name="unidade_consumidora" value={formData.unidade_consumidora} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Endereço</label>
                                <input type="text" name="endereco" value={formData.endereco} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Cidade / CEP</label>
                                <input type="text" name="cidade_cep" value={formData.cidade_cep} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Bandeira Tarifária</label>
                                <input type="text" name="bandeira" value={formData.bandeira} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Data Emissão</label>
                                <input type="date" name="data_emissao" value={formData.data_emissao} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-300 cursor-not-allowed dark:[color-scheme:dark]" readOnly />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Mês Referência</label>
                                <input type="text" name="mes_referencia" value={formData.mes_referencia} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors" placeholder="Ex: Janeiro/2024" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Número da Cobrança</label>
                                <input type="text" name="numero_cobranca" value={formData.numero_cobranca} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Leitura Anterior</label>
                                <input type="date" name="leitura_anterior" value={formData.leitura_anterior} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none dark:[color-scheme:dark] transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Leitura Atual</label>
                                <input type="date" name="leitura_atual" value={formData.leitura_atual} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none dark:[color-scheme:dark] transition-colors" />
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Resumo e Descontos */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">Resumo Financeiro</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Valor Total (R$)</label>
                                <input type="number" step="0.01" name="valor_total" value={formData.valor_total} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none font-bold transition-colors" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Valor Tarifa (Concessionária)</label>
                                <input type="number" step="0.00001" name="valor_tarifa_distribuidora" value={formData.valor_tarifa_distribuidora} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Data Vencimento</label>
                                <input type="date" name="data_vencimento" value={formData.data_vencimento} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none dark:[color-scheme:dark] transition-colors" required />
                            </div>
                        </div>

                        {/* Toggle Desconto */}
                        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Há desconto por data de quitação?</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" name="tem_desconto_quitacao" checked={formData.tem_desconto_quitacao} onChange={handleInputChange} className="sr-only peer" />
                                <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                            </label>
                        </div>

                        {formData.tem_desconto_quitacao && (
                            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 font-semibold text-xs uppercase">
                                        <tr>
                                            <th className="px-3 py-2 text-left">Data Pgt</th>
                                            <th className="px-3 py-2 text-right">Desc. (%)</th>
                                            <th className="px-3 py-2 text-right">Desc. (R$)</th>
                                            <th className="px-3 py-2 text-right">A Pagar (R$)</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {[1, 2, 3].map(i => (
                                            <tr key={i}>
                                                <td className="p-2"><input type="date" name={`desc_quitacao_data_pgt_${i}`} value={(formData as any)[`desc_quitacao_data_pgt_${i}`]} onChange={handleInputChange} className="w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-gray-900 dark:text-white dark:[color-scheme:dark]" /></td>
                                                <td className="p-2"><input type="number" step="0.01" name={`desc_quitacao_desconto_percent_${i}`} value={(formData as any)[`desc_quitacao_desconto_percent_${i}`]} onChange={handleInputChange} className="w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-right text-gray-900 dark:text-white" placeholder="%" /></td>
                                                <td className="p-2"><input type="number" step="0.01" name={`desc_quitacao_desconto_valor_${i}`} value={(formData as any)[`desc_quitacao_desconto_valor_${i}`]} onChange={handleInputChange} className="w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-right text-gray-900 dark:text-white" placeholder="R$" /></td>
                                                <td className="p-2"><input type="number" step="0.01" name={`desc_quitacao_a_pagar_${i}`} value={(formData as any)[`desc_quitacao_a_pagar_${i}`]} onChange={handleInputChange} className="w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-right font-bold text-gray-900 dark:text-white" placeholder="R$" /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Section 3: Detalhes da Cobrança */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">Detalhes da Cobrança</h3>
                        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                            <table className="w-full text-sm min-w-[600px] bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                                <thead className="bg-[#4D5D40] dark:bg-[#3d4a33] text-white text-xs uppercase font-bold">
                                    <tr>
                                        <th className="px-4 py-3 text-left w-1/2">Descrição</th>
                                        <th className="px-4 py-3 text-right">Quantidade</th>
                                        <th className="px-4 py-3 text-right">Tarifa</th>
                                        <th className="px-4 py-3 text-right">Valor (R$)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {/* Injeção */}
                                    <tr>
                                        <td className="px-4 py-2 font-medium">Injeção SCEE - Energia</td>
                                        <td className="px-4 py-2"><input type="number" step="0.001" name="detalhes_injecao_scee_qtd" value={formData.detalhes_injecao_scee_qtd} onChange={handleInputChange} className="w-full text-right border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1 text-orange-500 font-bold" /></td>
                                        <td className="px-4 py-2"><input type="number" step="0.000001" name="detalhes_injecao_scee_tarifa" value={formData.detalhes_injecao_scee_tarifa} onChange={handleInputChange} className="w-full text-right border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1 text-orange-500 font-bold" /></td>
                                        <td className="px-4 py-2"><input type="number" step="0.01" name="detalhes_injecao_scee_valor" value={formData.detalhes_injecao_scee_valor} onChange={handleInputChange} className="w-full text-right border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1 text-orange-500 font-bold" /></td>
                                    </tr>
                                    {/* Adicional Bandeira */}
                                    <tr>
                                        <td className="px-4 py-2 font-medium">Adicional de bandeira</td>
                                        <td className="px-4 py-2"><input type="number" step="0.001" name="detalhes_adc_bandeira_qtd" value={formData.detalhes_adc_bandeira_qtd} onChange={handleInputChange} className="w-full text-right border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1 text-orange-500 font-bold" /></td>
                                        <td className="px-4 py-2"><input type="number" step="0.000001" name="detalhes_adc_bandeira_tarifa" value={formData.detalhes_adc_bandeira_tarifa} onChange={handleInputChange} className="w-full text-right border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1 text-orange-500 font-bold" /></td>
                                        <td className="px-4 py-2"><input type="number" step="0.01" name="detalhes_adc_bandeira_valor" value={formData.detalhes_adc_bandeira_valor} onChange={handleInputChange} className="w-full text-right border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1 text-orange-500 font-bold" /></td>
                                    </tr>
                                    {/* Imposto */}
                                    <tr>
                                        <td className="px-4 py-2 font-medium" colSpan={3}>Imposto não compensado INJEÇÃO SCEE</td>
                                        <td className="px-4 py-2"><input type="number" step="0.01" name="detalhes_imposto_nao_comp_valor" value={formData.detalhes_imposto_nao_comp_valor} onChange={handleInputChange} className="w-full text-right border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1 text-orange-500 font-bold" /></td>
                                    </tr>
                                    {/* Energia Comp Não Isenta */}
                                    <tr>
                                        <td className="px-4 py-2 font-medium" colSpan={3}>Energia Comp não isenta (Tributos)</td>
                                        <td className="px-4 py-2"><input type="number" step="0.01" name="detalhes_energia_comp_nao_isenta_valor" value={formData.detalhes_energia_comp_nao_isenta_valor} onChange={handleInputChange} className="w-full text-right border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1 text-orange-500 font-bold" /></td>
                                    </tr>
                                    {/* Energia Compensada GD2 */}
                                    <tr>
                                        <td className="px-4 py-2 font-medium">Energia Compensada em GD 2</td>
                                        <td className="px-4 py-2"><input type="number" step="0.001" name="detalhes_energia_comp_gd2_qtd" value={formData.detalhes_energia_comp_gd2_qtd} onChange={handleInputChange} className="w-full text-right border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1 text-orange-500 font-bold" /></td>
                                        <td className="px-4 py-2"></td>
                                        <td className="px-4 py-2"><input type="number" step="0.01" name="detalhes_energia_comp_gd2_valor" value={formData.detalhes_energia_comp_gd2_valor} onChange={handleInputChange} className="w-full text-right border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1 text-orange-500 font-bold" /></td>
                                    </tr>
                                    {/* Outros */}
                                    <tr>
                                        <td className="px-4 py-2 font-medium" colSpan={3}>Outros (1)</td>
                                        <td className="px-4 py-2"><input type="number" step="0.01" name="detalhes_outros_1_valor" value={formData.detalhes_outros_1_valor} onChange={handleInputChange} className="w-full text-right border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1 text-orange-500 font-bold" /></td>
                                    </tr>
                                    <tr>
                                        <td className="px-4 py-2 font-medium" colSpan={3}>Outros (2)</td>
                                        <td className="px-4 py-2"><input type="number" step="0.01" name="detalhes_outros_2_valor" value={formData.detalhes_outros_2_valor} onChange={handleInputChange} className="w-full text-right border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1 text-orange-500 font-bold" /></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Section 4: Detalhes Adicionais e Anexos */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">Outras Informações</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Consumo Mês (kWh)</label>
                                <input type="number" step="0.01" name="consumo_mes" value={formData.consumo_mes} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Energia Compensada (kWh)</label>
                                <input type="number" step="0.01" name="energia_compensada" value={formData.energia_compensada} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Economia (R$)</label>
                                <input type="number" step="0.01" name="economia" value={formData.economia} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm text-success font-bold transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Valor Sem Desconto</label>
                                <input type="number" step="0.01" name="valor_sem_desconto" value={formData.valor_sem_desconto} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Valor Com Desconto</label>
                                <input type="number" step="0.01" name="valor_com_desconto" value={formData.valor_com_desconto} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm font-bold transition-colors" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Cód. Barras (Números)</label>
                                <input type="text" name="codigo_barras" value={formData.codigo_barras} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm transition-colors" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-4 text-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                                <label className="cursor-pointer block w-full h-full">
                                    <div className="text-gray-500 dark:text-gray-400 mb-2 font-medium">Anexar Código de Barras</div>
                                    <Upload className="mx-auto text-gray-400 dark:text-gray-500 mb-2" />
                                    <span className="text-xs text-primary font-semibold">Clique para upload</span>
                                    <input type="file" className="hidden" onChange={(e) => handleFileChange(e, 'codigo_barras')} />
                                    {files.codigo_barras && <p className="text-xs text-green-600 mt-2 truncate">{files.codigo_barras.name}</p>}
                                </label>
                            </div>
                            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-4 text-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                                <label className="cursor-pointer block w-full h-full">
                                    <div className="text-gray-500 dark:text-gray-400 mb-2 font-medium">Anexar QR Code PIX</div>
                                    <Upload className="mx-auto text-gray-400 dark:text-gray-500 mb-2" />
                                    <span className="text-xs text-primary font-semibold">Clique para upload</span>
                                    <input type="file" className="hidden" onChange={(e) => handleFileChange(e, 'qr_code')} />
                                    {files.qr_code && <p className="text-xs text-green-600 mt-2 truncate">{files.qr_code.name}</p>}
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                        <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            Cancelar
                        </button>
                        <button type="submit" disabled={loading} className="px-6 py-2.5 rounded-xl bg-primary text-white font-bold hover:bg-primary-dark transition-colors shadow-lg shadow-primary/30 flex items-center gap-2">
                            {loading ? 'Salvando...' : <><CheckCircle2 size={18} /> Salvar Boleto</>}
                        </button>
                    </div>

                </form>
            </div>
        </div>,
        document.body
    );
};

export default BoletoModal;
